/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import { getTemplate, getTemplateChildren } from "@/utils/dom/component";
import { commonAllowedTags, escapeHtml } from "@/utils/escaper";
import { Component } from "./component";

let messageTemplate: HTMLTemplateElement;

export class MessageList extends Component<MessageListEvents> {
    static selector = 'messages';
    static templateSelector = 'message';

    readonly selectors = {
        messageList: '[data-role="message-list"]',
        title: '[data-message="title"]',
        text: '[data-message="text"]',
        actions: '[data-message="actions"]'
    };

    readonly classes = {
        messageList: 'messages',
        message: 'message',
        content: 'message__content',
        title: 'message__title',
        text: 'message__text',
        actions: 'message__actions'
    };

    readonly dismissButton: MessageAction = {
        text: window.themeConfig.i18n.dismiss || 'Dismiss',
        classes: ['dismiss'],
        action: 'dismiss'
    };

    public element: HTMLElement;

    protected listElement: HTMLElement;
    protected messageTemplate: HTMLTemplateElement;

    protected messages = new WeakMap<MessageInterface, Element>();

    constructor(element: HTMLElement) {
        super();

        this.element = element;

        if (!messageTemplate) {
            messageTemplate = this._createTemplate();
        }

        this.listElement = this.element.querySelector<HTMLElement>(this.selectors.messageList)
            || this._createListElement();
        this.messageTemplate = getTemplate(MessageList.templateSelector, this.element) || messageTemplate;
    }

    /**
     * @inheritdoc
     */
    public override destroy(): void {
        super.destroy();

        this.listElement.innerHTML = '';
        this.listElement = null as any;
        this.messageTemplate = null as any;
        this.element = null as any;
    }

    /**
     * Add new message.
     * Returns callback to dismiss the message.
     */
    public addMessage(message: MessageInterface): () => void {
        message.actions = message.actions || [];
        if (message.dismiss !== false) {
            message.actions.push({ ...this.dismissButton });
        }

        const hasActions = !(message.dismiss !== false && message.actions.length === 1);

        const element = getTemplateChildren(this.messageTemplate)[0];
        let titleElement = element.querySelector<HTMLElement>(this.selectors.title)
        let textElement = element.querySelector<HTMLElement>(this.selectors.text);
        let actionsElement = element.querySelector<HTMLElement>(this.selectors.actions);

        if (!titleElement) {
            titleElement = this._createTitleElement();
            element.prepend(titleElement);
        }
        if (!textElement) {
            textElement = this._createTextElement();
            titleElement.after(textElement);
        }
        if (!actionsElement) {
            actionsElement = this._createActionsElement();
            element.append(actionsElement);
        }

        element.classList.add('message', message.type);
        element.classList.add(hasActions ? 'expanded' : 'inline');
        if (message.classes?.length) {
            element.classList.add(...message.classes.filter(className => className));
        }

        textElement.innerHTML = escapeHtml(message.text, commonAllowedTags);
        if (message.title) {
            titleElement.innerHTML = escapeHtml(message.title, commonAllowedTags);
        } else {
            // use title element for single text
            titleElement.innerHTML = textElement.innerHTML;
            textElement.remove();
        }

        if (message.actions.length) {
            message.actions.forEach(action => {
                actionsElement.append(this._createButton(action, message));
            });
        } else {
            actionsElement.remove();
        }

        this.messages.set(message, element);
        this.listElement.classList.remove('empty');
        this.listElement.append(element);

        this.trigger('messageAdded', message);

        return () => this.dismissMessage(message);
    }

    /**
     * Add multiple messages
     */
    public addMessages(messages: MessageInterface[]): (() => void)[] {
        return messages.map(message => this.addMessage(message));
    }

    /**
     * Add error message
     */
    public addErrorMessage(message: string) {
        return this.addMessage({text: message, type: 'error'});
    }

    /**
     * Add warning message
     */
    public addWarningMessage(message: string) {
        return this.addMessage({text: message, type: 'warning'});
    }

    /**
     * Add notice message
     */
    public addNoticeMessage(message: string) {
        return this.addMessage({text: message, type: 'notice'});
    }

    /**
     * Add success message
     */
    public addSuccessMessage(message: string) {
        return this.addMessage({text: message, type: 'success'});
    }

    /**
     * Dismiss an added message
     */
    public dismissMessage(message: MessageInterface): void {
        const element = this.messages.get(message);
        if (element) {
            element.remove();
            this.messages.delete(message);
            this.trigger('messageDismissed', message);
        }

        if (!this.listElement.childElementCount) {
            this.listElement.classList.add('empty');
        }
    }

    /**
     * Create button for message action
     */
    protected _createButton(
        {text, classes, action}: MessageAction,
        message: MessageInterface
    ): HTMLElement {
        const button = document.createElement('button');
        const span = document.createElement('span');

        classes = classes || [];

        span.textContent = text;
        button.type = 'button';
        button.classList.add('button');
        button.title = text;
        button.append(span);

        if (classes?.length) {
            button.classList.add(...classes.filter(className => className));
        }

        if (action) {
            const callback = typeof action === 'string'
                ? this._getAction(action)
                : action;
            button.addEventListener('click', () => callback(message));
        }

        return button;
    }

    protected _getAction(type: MessageActionAction | string): MessageActionCallback {
        switch (type) {
            case 'dismiss':
                return (message) => this.dismissMessage(message);
        }

        return () => {};
    }

    /**
     * Create message list element
     */
    protected _createListElement(): HTMLElement {
        const element = document.createElement('div');

        element.role = "alert";
        element.ariaAtomic = 'true';
        element.setAttribute('data-role', 'message-list');
        element.classList.add(this.classes.messageList);

        this.element.prepend(element);
        return element;
    }

    /**
     * Create message template
     */
    protected _createTemplate(): HTMLTemplateElement {
        const template = document.createElement('template');
        const element = document.createElement('div');
        const content = document.createElement('div');
        const title = this._createTitleElement();
        const text = this._createTextElement();
        const actions = this._createActionsElement();

        content.classList.add(this.classes.content);
        content.append(title, text);

        element.classList.add(this.classes.message);
        element.setAttribute('data-message', 'message');
        element.append(content, actions);

        template.content.append(element);
        return template;
    }

    protected _createTitleElement(): HTMLElement {
        const element = document.createElement('div');

        element.classList.add(this.classes.title);
        element.setAttribute('data-message', 'title');
        return element;
    }

    protected _createTextElement(): HTMLElement {
        const element = document.createElement('div');

        element.classList.add(this.classes.text);
        element.setAttribute('data-message', 'text');
        return element;
    }

    protected _createActionsElement(): HTMLElement {
        const element = document.createElement('div');
        element.classList.add(this.classes.actions);
        element.setAttribute('data-message', 'actions');

        return element;
    }
}

export interface MessageListEvents {
    messageAdded: MessageInterface
    messageDismissed: MessageInterface
}

export type MessageType = 'info' | 'error' | 'notice' | 'warning' | 'success';

export interface MessageInterface {
    text: string
    type: MessageType
    title?: string
    classes?: string[]
    dismiss?: boolean
    actions?: MessageAction[]
}

export interface MessageAction {
    text: string
    classes?: string[]
    action?: MessageActionCallback | MessageActionAction
}

export type MessageActionCallback = (message: MessageInterface) => void;
export type MessageActionAction = 'dismiss';
